import { jsx as _jsx } from "react/jsx-runtime";
import { useMemo, useState } from 'react';
import * as Sentry from '@sentry/react';
import { useOnMount } from '~/hooks/useOnMount';
import { useLoadStripe } from '~/infra/useLoadStripe';
import { useIoContext } from './IoContext';
const FONT_URL = 'https://storage.googleapis.com/anchor-cdn/fonts/Lufga-Regular.woff2';
/**
 * Stripe Elements render inside an iframe on js.stripe.com. Loading fonts via a
 * URL requires the font server to include js.stripe.com in its CORS policy. Our
 * GCS bucket allows app.sayanchor.com but not js.stripe.com, so the font request
 * from Stripe's iframe is silently blocked.
 *
 * Workaround: the parent page *can* fetch the font (CORS allows our origin), so
 * we fetch it here, convert to a data: URI, and hand that to Stripe. Data URIs
 * require no cross-origin request from the iframe.
 *
 * The fetch starts at module-load time (before any component mounts) and is
 * served from the browser cache because the page already loads Lufga-Regular via
 * CSS @font-face from the same URL. Because Stripe reads `fonts` only once when
 * the Elements instance is created, the data URI must be available synchronously
 * before the first render — `resolvedFontSrc` captures the result as soon as the
 * promise settles so `useState` can initialise with it on a cache hit.
 */ function arrayBufferToBase64(buffer) {
    const bytes = new Uint8Array(buffer);
    const chunks = [];
    const chunkSize = 8192;
    for(let i = 0; i < bytes.length; i += chunkSize){
        chunks.push(String.fromCharCode(...bytes.subarray(i, i + chunkSize)));
    }
    return btoa(chunks.join(''));
}
let resolvedFontSrc = null;
// Guard against environments without `fetch` (e.g. jsdom in unit tests). Tests
// don't exercise Stripe's font rendering, so falling back to the URL-based
// config is harmless there.
const fontDataUriPromise = typeof fetch === 'function' ? fetch(FONT_URL).then((res)=>res.arrayBuffer()).then((buf)=>{
    resolvedFontSrc = "data:font/woff2;base64,".concat(arrayBufferToBase64(buf));
    return resolvedFontSrc;
}).catch((err)=>{
    Sentry.captureException(err);
    return null;
}) : Promise.resolve(null);
function useFontDataUri() {
    const [fontSrc, setFontSrc] = useState(resolvedFontSrc);
    useOnMount(()=>{
        let active = true;
        fontDataUriPromise.then((src)=>{
            if (active) setFontSrc(src);
        });
        return ()=>{
            active = false;
        };
    });
    return fontSrc;
}
function buildElementsOptions(fontDataUri) {
    const src = fontDataUri != null ? "url(".concat(fontDataUri, ")") : "url(".concat(FONT_URL, ")");
    return {
        fonts: [
            {
                family: 'Lufga-Regular',
                src
            }
        ]
    };
}
export default function StripeElementsProvider(props) {
    const { reactStripe: { Elements } } = useIoContext();
    const stripeObject = useLoadStripe();
    const fontDataUri = useFontDataUri();
    const elementsOptions = useMemo(()=>buildElementsOptions(fontDataUri), [
        fontDataUri
    ]);
    return /*#__PURE__*/ _jsx(Elements, {
        stripe: stripeObject,
        options: elementsOptions,
        children: props.children
    });
}
